import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import {
  PRACTICE_MOVER_TAG,
  cosmeticValue,
  practiceMoverPose,
  practiceMovers,
  type DynBox,
  type MapDef,
  type PracticeMover,
} from "@neon-purge/shared";
import { Avatar } from "./avatar.js";
import { mapTextures, worldProjectUV } from "./mapTextures.js";
import { DragonMesh, PickupMesh, SkyhavenDiscoBallPlatform, VehicleMesh, WormMesh } from "./worldEntities.js";
import { giantBoombox, giantTurntable } from "./decor.js";
import { MonsterMesh } from "./monsters.js";
import { buildEuropeanDragon, type DragonRig } from "./ambientLife.js";
import { realModelsEnabled } from "./gltfAssets.js";
import { loadHorseModel } from "./horseModel.js";
import { loadCamelModel } from "./camelModel.js";
import { dragonModelLoaded, loadDragonModel, mountDragonModel, type DragonModel } from "./dragonModel.js";
import { KRAKEN_URL, preloadSeaModel, seaModelIfReady } from "./seaModels.js";

/** `localStorage neonpurge.geardemo = "cowboy,pirate,skull,jetpack,parachute"`
 *  (or "all") — see CharacterPreview.buildAvatar. Read ONCE at module load. */
const GEAR_DEMO = (() => {
  try { return localStorage.getItem("neonpurge.geardemo") || ""; } catch { return ""; }
})();
/** `localStorage neonpurge.gearspin = "<radians>"` — PIN the cosmetics
 *  turntable instead of letting it rotate at 0.5 rad/s (agent-only, 2026-08-06).
 *  The dais is the only fixed-camera avatar stage in the game, but its yaw is
 *  driven by wall-clock, so a before/after pair photographed 40 s apart came
 *  back at two different angles — which made the WORN-GEAR A/B this pass needed
 *  two photographs of two different things rather than a measurement. 0 faces
 *  the camera; Math.PI shows the back. */
const GEAR_SPIN: number | null = (() => {
  try {
    const raw = localStorage.getItem("neonpurge.gearspin");
    if (raw === null || raw === "") return null;
    const v = Number(raw);
    return Number.isFinite(v) ? v : null;  // 0 is a legal pin (facing camera)
  } catch { return null; }
})();

type PreviewAttachment = { mover: PracticeMover; off: [number, number, number] };
type PreviewPickup = { m: PickupMesh; x: number; y: number; z: number; attach?: PreviewAttachment };
type PreviewVehicle = { m: VehicleMesh; x: number; y: number; z: number; yaw: number; attach?: PreviewAttachment };

/**
 * Small self-contained 3D preview renderers that draw onto their OWN canvas
 * (embedded in a UI panel), independent of the main game renderer. Used for
 * the map picker (orbiting map preview) and the character customiser (rotating
 * avatar that reflects cosmetics + skin live). Always dispose() when removed.
 */

// ---- POOLED preview GL contexts ----
// Every MapPreview3D / CharacterPreview used to create a brand-new WebGL
// context on its canvas and forceContextLoss() it on close. Measured
// (agent/bench-menu-perf-live.mjs): opening Play paid ~1.2 s of context
// creation + a full shader recompile EVERY visit, Cosmetics ~0.6 s. Now a few
// offscreen contexts live for the whole session: a preview borrows one, renders
// into it and copies the frame onto its own canvas with a 2D drawImage (a
// GPU-side blit for these small tiles). Compiled programs survive between
// visits, so the second open of a screen compiles nothing.
interface PooledGl { renderer: THREE.WebGLRenderer; busy: boolean; aa: boolean; lost: boolean }
const glPool: PooledGl[] = [];
// harness readout (agent/bench-menu-perf-live.mjs): one property write at load
(window as unknown as { npPreviewPool?: PooledGl[] }).npPreviewPool = glPool;
const GL_POOL_KEEP = 3; // idle contexts kept warm (browsers cap live contexts ~16)
function acquirePreviewGl(aa: boolean): PooledGl {
  let e = glPool.find((p) => !p.busy && !p.lost && p.aa === aa);
  if (!e) {
    const c = document.createElement("canvas");
    const renderer = new THREE.WebGLRenderer({ canvas: c, antialias: aa, alpha: true, powerPreference: "high-performance" });
    // parallel shader compile: lets three poll completion instead of blocking
    try { renderer.getContext().getExtension("KHR_parallel_shader_compile"); } catch { /* absent */ }
    const entry: PooledGl = { renderer, busy: false, aa, lost: false };
    c.addEventListener("webglcontextlost", () => { entry.lost = true; });
    glPool.push(entry);
    e = entry;
  }
  e.busy = true;
  return e;
}
function releasePreviewGl(e: PooledGl): void {
  e.busy = false;
  e.renderer.renderLists.dispose(); // drop refs to the closed preview's scene
  // drop lost contexts, and only the idle ones BEYOND the keep count (this
  // used to free every idle context — the whole warm program cache — as soon
  // as one too many was idle)
  let kept = 0;
  for (const p of glPool.filter((q) => !q.busy)) {
    if (!p.lost && ++kept <= GL_POOL_KEEP) continue;
    glPool.splice(glPool.indexOf(p), 1);
    p.renderer.dispose();
    if (!p.lost) p.renderer.forceContextLoss();
  }
}
/** Debounced canvas sizing. Reallocating a WebGL drawing buffer is the single
 *  biggest cost of opening a preview (bench-menu-perf-live: ~1-2 s of
 *  setSize while the Play screen's layout settled through its entrance
 *  animation, one realloc per intermediate size). A new size is applied only
 *  once it has held for ~120 ms — and, unlike the old aspect-only check, a
 *  same-aspect size change (400x300 -> 800x600) now applies too. */
interface SizeState { w: number; h: number; pw: number; ph: number; since: number }
/** True when (w, h) should be applied NOW (caller does the resize). */
function settleSize(st: SizeState, w: number, h: number, now: number): boolean {
  if (w === st.w && h === st.h) return false;
  if (w !== st.pw || h !== st.ph) { st.pw = w; st.ph = h; st.since = now; }
  // first size ever applies at once (nothing on screen yet to protect)
  if (st.w !== 0 && now - st.since < 120) return false;
  st.w = w; st.h = h;
  return true;
}

/** Copy the pooled context's frame onto the preview's own canvas. */
function blitPreview(gl: THREE.WebGLRenderer, ctx: CanvasRenderingContext2D | null): void {
  if (!ctx) return;
  const src = gl.domElement;
  const dst = ctx.canvas;
  if (dst.width !== src.width || dst.height !== src.height) { dst.width = src.width; dst.height = src.height; }
  ctx.clearRect(0, 0, dst.width, dst.height);
  ctx.drawImage(src, 0, 0);
}

export class MapPreview3D {
  private renderer: THREE.WebGLRenderer;
  private gl: PooledGl;
  private blit: CanvasRenderingContext2D | null;
  /** A freshly built map scene compiles its programs ASYNC (parallel compile)
   *  — until then the canvas keeps showing the previous frame, instead of the
   *  first render blocking the menu on a synchronous compile (measured ~0.5-2 s
   *  per map cycle). Token-guarded so a fast cycle ignores stale completions. */
  private compileToken = 0;
  private compiling = false;
  private sizeSt: SizeState = { w: 0, h: 0, pw: 0, ph: 0, since: 0 };
  private compiled: Promise<void> = Promise.resolve();
  /** Resolves once the current map's programs are compiled (or gave up). */
  whenCompiled(): Promise<void> { return this.compiled; }
  private scene = new THREE.Scene();
  private camera: THREE.PerspectiveCamera;
  private mapGroup: THREE.Group | null = null;
  // the LIVING preview: real pickups, vehicles, monsters + the dragon
  private extraPickups: PreviewPickup[] = [];
  private extraVehicles: PreviewVehicle[] = [];
  private extraMonsters: MonsterMesh[] = [];
  private previewDragon: DragonMesh | null = null;
  private previewWorm: WormMesh | null = null;
  private previewDisco: SkyhavenDiscoBallPlatform | null = null;
  /** Throwaway collider sink for the preview disco — the lobby needs the mesh
   *  animated, not its colliders. Reused so the preview loop stays alloc-free. */
  private previewDiscoDyn: DynBox[] = [];
  private previewDiscoPool: DynBox[] = [];
  private previewPracticePlatforms: { group: THREE.Group; movers: PracticeMover[] } | null = null;
  private dragonAng = 0;
  /** Medieval: the three circling european dragons, shown in the preview too. */
  private euroDragons: { rig: DragonRig; r: number; y: number; speed: number; a: number; tint: string; model: DragonModel | null }[] = [];
  /** 🎲 Random-map option: a spinning neon "?" instead of a map. */
  private randomMark: THREE.Group | null = null;
  private loadedMapId = "";

  private clearRandomMark(): void {
    if (this.randomMark) { this.scene.remove(this.randomMark); this.randomMark = null; }
  }

  private clearPracticePlatforms(): void {
    if (!this.previewPracticePlatforms) return;
    const group = this.previewPracticePlatforms.group;
    this.scene.remove(group);
    group.traverse((o) => {
      const mesh = o as THREE.Mesh;
      mesh.geometry?.dispose?.();
      const mat = mesh.material;
      if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
      else mat?.dispose?.();
    });
    this.previewPracticePlatforms = null;
  }

  /** Show a rotating "?" for the Random Map entry (no real map to preview). */
  showRandom(): void {
    if (this.loadedMapId === "__random__") return;
    this.loadedMapId = "__random__";
    if (this.mapGroup) { this.scene.remove(this.mapGroup); this.deepDispose(this.mapGroup); this.mapGroup = null; }
    this.previewWorm?.dispose(this.scene);
    this.previewWorm = null; // Wild West -> Random used to keep the sandworm behind the "?" 
    for (const pk of this.extraPickups) pk.m.dispose(this.scene);
    this.extraPickups = [];
    for (const v of this.extraVehicles) v.m.dispose(this.scene);
    this.extraVehicles = [];
    for (const m of this.extraMonsters) m.dispose(this.scene);
    this.extraMonsters = [];
    this.previewDragon?.dispose(this.scene); this.previewDragon = null;
    this.previewDisco?.dispose(); this.previewDisco = null;
    this.clearPracticePlatforms();
    // deep-dispose, not just detach — loadMap does the same. Cycling
    // medieval_castle → 🎲 Random otherwise leaked the three dragon rigs' GPU
    // geometries/materials for the preview's lifetime.
    for (const d of this.euroDragons) {
      d.model?.dispose(); // per-instance pieces; the GLB's shared ones stay cached
      d.model = null;
      this.scene.remove(d.rig.group);
      this.deepDispose(d.rig.group);
    }
    this.euroDragons = [];
    this.clearRandomMark();
    const cvs = document.createElement("canvas"); cvs.width = cvs.height = 256;
    const c = cvs.getContext("2d")!;
    c.font = "bold 210px sans-serif"; c.textAlign = "center"; c.textBaseline = "middle";
    c.lineWidth = 16; c.strokeStyle = "#ff2fd0"; c.strokeText("?", 128, 140);
    c.fillStyle = "#00f0ff"; c.fillText("?", 128, 140);
    const tex = new THREE.CanvasTexture(cvs); tex.colorSpace = THREE.SRGBColorSpace;
    const mat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, side: THREE.DoubleSide, depthWrite: false });
    const g = new THREE.Group();
    for (const ry of [0, Math.PI / 2]) { const p = new THREE.Mesh(new THREE.PlaneGeometry(18, 18), mat); p.rotation.y = ry; g.add(p); }
    g.position.set(0, 6, 0);
    this.scene.add(g);
    this.randomMark = g;
    this.center.set(0, 6, 0); this.radius = 32; this.height = 0; this.userAngle = Math.PI / 2;
  }
  private lights = new THREE.Group();
  private running = false;
  private raf = 0;
  private t = 0;
  private radius = 60;
  private height = 40;
  private center = new THREE.Vector3();
  private userAngle: number | null = null;
  private dragging = false;
  private lastX = 0;

  /** Quality profile: weak machines keep the SAME 3D preview, just cheaper —
   *  no MSAA, fewer pixels, half the frame rate. Never a flat fallback. */
  private frameDiv = 1;
  private frameNo = 0;
  private noEdges = false;

  constructor(private canvas: HTMLCanvasElement, opts: { antialias?: boolean; pixelRatio?: number; frameDivider?: number; noEdges?: boolean } = {}) {
    this.frameDiv = Math.max(1, opts.frameDivider ?? 1);
    this.noEdges = opts.noEdges ?? false;
    this.gl = acquirePreviewGl(opts.antialias ?? true);
    this.renderer = this.gl.renderer;
    this.blit = canvas.getContext("2d");
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, opts.pixelRatio ?? 1)); // small tile - save the GPU
    // no setSize here: the canvas is usually not mounted yet (0x0 -> a 400x300
    // placeholder realloc, then a second one at the real size). The loop's
    // settleSize applies the real size on the first mounted frame.
    this.camera = new THREE.PerspectiveCamera(50, canvas.clientWidth / canvas.clientHeight, 0.5, 2000);
    this.scene.add(this.lights);
    // drag to orbit
    canvas.addEventListener("pointerdown", (e) => { this.dragging = true; this.lastX = e.clientX; this.userAngle = this.userAngle ?? this.t * 0.25; });
    // window-level listeners MUST be removed in dispose() — they retained the
    // whole preview (scene + renderer) forever, once per open/close
    this.onPointerUp = () => (this.dragging = false);
    this.onPointerMove = (e: PointerEvent) => {
      if (!this.dragging) return;
      this.userAngle = (this.userAngle ?? 0) + (e.clientX - this.lastX) * 0.01;
      this.lastX = e.clientX;
    };
    window.addEventListener("pointerup", this.onPointerUp);
    window.addEventListener("pointermove", this.onPointerMove);
  }

  private onPointerUp: () => void = () => {};
  private onPointerMove: (e: PointerEvent) => void = () => {};

  private previewMoverAttachment(
    movers: PracticeMover[],
    x: number,
    y: number,
    z: number,
    margin = 2.2,
  ): PreviewAttachment | undefined {
    let best: { mover: PracticeMover; off: [number, number, number]; dy: number } | undefined;
    for (const mover of movers) {
      for (const part of mover.parts) {
        if (part.s[1] > 4) continue;
        const cx = mover.base[0] + part.offset[0];
        const cy = mover.base[1] + part.offset[1];
        const cz = mover.base[2] + part.offset[2];
        const top = cy + part.s[1] / 2;
        if (Math.abs(x - cx) > part.s[0] / 2 + margin) continue;
        if (Math.abs(z - cz) > part.s[2] / 2 + margin) continue;
        const dy = y - top;
        if (dy < -1.8 || dy > 5.5) continue;
        if (!best || Math.abs(dy) < Math.abs(best.dy)) {
          best = { mover, off: [x - mover.base[0], y - mover.base[1], z - mover.base[2]], dy };
        }
      }
    }
    return best ? { mover: best.mover, off: best.off } : undefined;
  }

  /** Deep-free a subtree: geometry AND materials (shared textures skipped).
   *  Geometry-only disposal leaked hundreds of per-box materials per pick. */
  private deepDispose(root: THREE.Object3D): void {
    root.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!(mesh.geometry as { userData?: { shared?: boolean } })?.userData?.shared) mesh.geometry?.dispose?.();
      const mats = Array.isArray(mesh.material) ? mesh.material : mesh.material ? [mesh.material] : [];
      for (const m of mats) {
        // a cached glTF's materials are SHARED with the game context: dispose()
        // would make every renderer drop its program for them (a cold compile
        // in the next match)
        if (m.userData?.shared) continue;
        const t = m as THREE.MeshStandardMaterial;
        if (t.map && !t.map.userData?.shared) t.map.dispose();
        m.dispose();
      }
    });
  }

  /** Free the current map and every living-preview entity (pickups,
   *  vehicles, monsters, dragons, worm, disco, platforms). */
  private clearMapContent(): void {
    if (this.mapGroup) { this.scene.remove(this.mapGroup); this.deepDispose(this.mapGroup); this.mapGroup = null; }
    for (const pk of this.extraPickups) pk.m.dispose(this.scene);
    this.extraPickups = [];
    for (const v of this.extraVehicles) v.m.dispose(this.scene);
    this.extraVehicles = [];
    for (const m of this.extraMonsters) m.dispose(this.scene);
    this.extraMonsters = [];
    this.previewDragon?.dispose(this.scene);
    this.previewDragon = null;
    this.previewWorm?.dispose(this.scene);
    this.previewWorm = null;
    this.previewDisco?.dispose();
    this.previewDisco = null;
    this.clearPracticePlatforms();
    for (const d of this.euroDragons) {
      d.model?.dispose(); // per-instance pieces; the GLB's shared ones stay cached
      d.model = null;
      this.scene.remove(d.rig.group);
      this.deepDispose(d.rig.group);
    }
    this.euroDragons = [];
  }

  loadMap(map: MapDef): void {
    // Mode toggles and lobby patches can select the already-visible map again.
    // Rebuilding hundreds of geometries in that case caused an apparent
    // random preview load pause and needless shader/resource churn.
    if (this.loadedMapId === map.id) return;
    this.loadedMapId = map.id;
    this.clearRandomMark();
    this.clearMapContent();
    this.lights.clear();
    const day = (map.theme ?? "neon") === "day";
    this.scene.background = new THREE.Color(day ? "#bfe3ff" : "#0a0f1c");
    this.scene.fog = new THREE.Fog(day ? "#cfe4f5" : "#05060a", 120, 480);
    this.lights.add(new THREE.HemisphereLight(day ? "#eaf3ff" : "#5a6a8c", day ? "#8a8570" : "#1a2030", day ? 1.6 : 2.4));
    const sun = new THREE.DirectionalLight(day ? "#fff2d0" : "#9fc4ff", day ? 2.2 : 1.5);
    sun.position.set(60, 120, 40);
    this.lights.add(sun);

    const tex = mapTextures();
    const group = new THREE.Group();
    const lineMat = new THREE.LineBasicMaterial({ color: day ? "#2a2a24" : "#3a4a6a", transparent: true, opacity: day ? 0.18 : 0.4 });
    // MERGED preview geometry — one mesh per MATERIAL FAMILY instead of one per
    // box, exactly like the in-game renderer.loadMap does. A dense preview
    // (medieval_castle = 962 boxes) was ~962 meshes + ~962 MeshStandardMaterial
    // instances + ~962 edge line-segments = hundreds of shader compiles and
    // ~1900 draw calls per rendered frame, most of the lobby "open freeze" on
    // slow iGPUs. Per-box tint lives in vertex colors so any colour shares a
    // family material; families collapse to a handful of merged meshes.
    const meshGeos = new Map<string, THREE.BufferGeometry[]>();
    const edgeGeos: THREE.BufferGeometry[] = [];
    const tintColor = new THREE.Color();
    for (const box of map.boxes) {
      if (box.tag === "nodraw" || box.tag === PRACTICE_MOVER_TAG) continue; // drawn by decor/dynamic systems
      const [w, h, dd] = box.s;
      const geo = new THREE.BoxGeometry(w, h, dd).toNonIndexed();
      const flatGround = h <= 1.2 && (w >= 8 || dd >= 8);
      // tall solid blocks get the WINDOW FACADE + lit windows, exactly like
      // the real renderer — the preview shows the same textured city
      const rustic = map.id === "wild_west_train_pursuit";
      const building = !rustic && !flatGround && h >= 5 &&
        (Math.min(w, dd) >= 2.5 || (map.id === "neo_island" && Math.max(w, dd) >= 6));
      worldProjectUV(geo, flatGround ? 6 : building ? 4.5 : 3.2);
      // bake the per-box tint into vertex colors (flat — no AO, so the merged
      // look is byte-identical to the old one-material-per-box path)
      tintColor.set(box.color ?? "#20263a"); // neon-dimension towers keep their neon colour
      const pos = geo.getAttribute("position");
      const col = new Float32Array(pos.count * 3);
      for (let vi = 0; vi < pos.count; vi++) { col[vi * 3] = tintColor.r; col[vi * 3 + 1] = tintColor.g; col[vi * 3 + 2] = tintColor.b; }
      geo.setAttribute("color", new THREE.BufferAttribute(col, 3));
      geo.translate(box.p[0], box.p[1], box.p[2]);
      const family = flatGround ? "ground" : building ? "building" : "other";
      (meshGeos.get(family) ?? meshGeos.set(family, []).get(family)!).push(geo);
      // wireframe edges DOUBLE the object + draw count — skip on weak GPUs
      // (EdgesGeometry is also CPU-heavy to build, part of the open freeze).
      // Bucketed into ONE merged LineSegments instead of one per box.
      if (!building && !this.noEdges) {
        const eg = new THREE.EdgesGeometry(new THREE.BoxGeometry(w, h, dd));
        eg.translate(box.p[0], box.p[1], box.p[2]);
        edgeGeos.push(eg);
      }
    }
    // ONE material per family, shared across the whole map (vertexColors carry
    // the per-box tint). Same texture/roughness/emissive choices as the old
    // per-box materials, so the merged mesh renders identically.
    const familyMat = (family: string): THREE.MeshStandardMaterial => {
      const building = family === "building";
      const flatGround = family === "ground";
      return new THREE.MeshStandardMaterial({
        color: "#ffffff",
        vertexColors: true,
        map: (flatGround ? (day ? tex.terrain : tex.concrete) : building ? tex.facade : tex.surface).map,
        roughness: flatGround ? 0.96 : building ? 0.7 : 0.85,
        metalness: day ? 0.05 : 0.12,
        emissiveMap: building ? tex.facadeGlow : undefined,
        emissive: building ? "#ffffff" : "#000000",
        emissiveIntensity: building ? (day ? 0.12 : 0.85) : 0,
      });
    };
    for (const [family, geos] of meshGeos) {
      const merged = mergeGeometries(geos);
      if (!merged) { console.error(`[preview] merge failed for family ${family} — skipped`); continue; }
      group.add(new THREE.Mesh(merged, familyMat(family)));
      for (const g of geos) g.dispose(); // the merged copy owns the data now
    }
    if (edgeGeos.length) {
      const merged = mergeGeometries(edgeGeos);
      if (merged) group.add(new THREE.LineSegments(merged, lineMat));
      for (const g of edgeGeos) g.dispose();
    } else {
      lineMat.dispose(); // no edges built (noEdges tile, or all-building map)
    }
    this.scene.add(group);
    this.mapGroup = group;

    const movers = practiceMovers(map);
    if (movers.length) {
      const moverRoot = new THREE.Group();
      for (const mover of movers) {
        const island = new THREE.Group();
        const [x, y, z] = practiceMoverPose(mover, this.t);
        island.position.set(x, y, z);
        for (const part of mover.parts) {
          const [w, h, dd] = part.s;
          const geo = new THREE.BoxGeometry(w, h, dd).toNonIndexed();
          const flatGround = h <= 1.2 && (w >= 8 || dd >= 8);
          // any TALL part is a wall — window facade skin, matching the real
          // flying tower (violet neon-dimension windows, solid look)
          const building = h >= 5;
          worldProjectUV(geo, flatGround ? 6 : building ? 4.5 : 3.2);
          const mesh = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({
            color: building ? "#ffffff" : (part.color ?? "#20263a"),
            map: (flatGround ? (day ? tex.terrain : tex.concrete) : building ? tex.facade : tex.surface).map,
            roughness: flatGround ? 0.96 : building ? 0.7 : 0.85,
            metalness: day ? 0.05 : 0.12,
            emissiveMap: building ? tex.facadeGlow : undefined,
            emissive: building ? "#9b5cff" : (part.glow ?? "#000000"),
            emissiveIntensity: building ? 0.95 : (part.glow ? 0.35 : 0),
          }));
          mesh.position.set(part.offset[0], part.offset[1], part.offset[2]);
          island.add(mesh);
          if (!building) {
            const edgeBox = new THREE.BoxGeometry(w, h, dd);
            const edge = new THREE.LineSegments(new THREE.EdgesGeometry(edgeBox), lineMat);
            edgeBox.dispose();
            edge.position.copy(mesh.position);
            island.add(edge);
          }
        }
        moverRoot.add(island);
      }
      this.scene.add(moverRoot);
      this.previewPracticePlatforms = { group: moverRoot, movers };
    }

    // the map's ITEMS as real spinning pickup meshes + parked vehicles +
    // resident monsters + the dragon: the preview shows the LIVING map
    for (const pk of map.pickups ?? []) {
      const x = pk.p[0], y = pk.p[1] + 0.6, z = pk.p[2];
      this.extraPickups.push({
        m: new PickupMesh(this.scene, pk.kind),
        x, y, z,
        attach: this.previewMoverAttachment(movers, x, y, z),
      });
    }
    for (const v of map.vehicles ?? []) {
      const vm = new VehicleMesh(this.scene, v.kind);
      const item = {
        m: vm,
        x: v.p[0],
        y: v.p[1],
        z: v.p[2],
        yaw: v.yaw,
        attach: this.previewMoverAttachment(movers, v.p[0], v.p[1], v.p[2], 3.0),
      };
      vm.update(item.x, item.y, item.z, item.yaw, 0, 0, 0, false);
      this.extraVehicles.push(item);
    }
    // CHEAP MODE (weak GPU): skip the decorative "living" extras — giant
    // monsters, dragons, the sandworm, the disco ball. They each build heavy
    // geometry + shaders (a big chunk of the open freeze on slow iGPUs). The
    // map layout, pickups and vehicles still render.
    if (map.id === "neo_island" && !this.noEdges) {
      // the giant boombox + turntable landmarks (same builders as in-game)
      giantBoombox(this.mapGroup, 0, -94, 0);
      giantTurntable(this.mapGroup, 0, 86, Math.PI);
      this.previewDisco = new SkyhavenDiscoBallPlatform(this.scene, map);
    }
    if (map.id === "wild_west_train_pursuit" && !this.noEdges) {
      // the SANDWORM breaching out of the desert — seed a curved trail so it
      // arcs nicely, then leave it fully surfaced for the still preview
      const worm = new WormMesh(this.scene);
      for (let s = 0; s < 80; s++) {
        const a = s * 0.045;
        worm.update(Math.cos(a) * 46 - 18, Math.sin(a) * 46 + 20, 0, 1, 0);
      }
      this.previewWorm = worm;
    }
    // THE FLYING GHOST SHIP — a violet galleon cruising its sky circuit
    // (pirate map). Mirrors the in-game Plane skin so the preview sells it.
    if (map.plane?.galleon) {
      const pc = map.plane;
      const ship = new THREE.Group();
      const violet = new THREE.MeshStandardMaterial({ color: "#7a4bd0", emissive: "#4a2a9a", emissiveIntensity: 0.5, roughness: 0.5, transparent: true, opacity: 0.9 });
      const sailMat = new THREE.MeshStandardMaterial({ color: "#b79cf0", emissive: "#6a4ac0", emissiveIntensity: 0.4, roughness: 0.6, transparent: true, opacity: 0.7, side: THREE.DoubleSide });
      const box = (w: number, h: number, d: number, m: THREE.Material, x: number, y: number, z: number): void => {
        const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), m); mesh.position.set(x, y, z); ship.add(mesh);
      };
      box(6, 3, 20, violet, 0, 0, 0); box(4, 2.4, 5, violet, 0, 0.3, 12); box(2, 1.8, 3, violet, 0, 0.6, 15);
      box(6.5, 3.2, 4, violet, 0, 1.6, -9); box(6.2, 0.4, 20, violet, 0, 1.6, 0);
      for (const mz of [-4, 4]) {
        box(0.5, 16, 0.5, violet, 0, 9, mz); box(9, 0.3, 0.3, violet, 0, 12, mz);
        box(7, 5, 0.2, sailMat, 0, 9.5, mz + 0.3); box(6, 4, 0.2, sailMat, 0, 15, mz + 0.3);
      }
      ship.scale.setScalar(1.5);
      ship.position.set(pc.cx + pc.radius, pc.baseY, pc.cz);
      this.mapGroup.add(ship);
    }
    // THE KRAKEN lurking in the open water — same spot it spawns in-game, so the
    // preview sells the boss. Scaled up so it reads at preview distance.
    if (map.id === "pirate_ship" && !this.noEdges) {
      const kr = new MonsterMesh(this.scene, "kraken");
      kr.update(30, -2.2, 40, Math.PI * 0.25, 0, 0, 1); // dt=1 snaps it into place
      kr.group.scale.setScalar(1.6);
      this.extraMonsters.push(kr);
    }
    // the BIG UFO cruising its circuit (Skyhaven landmark)
    if (map.plane?.deckR) {
      const pc = map.plane;
      const saucer = new THREE.Group();
      const hull = new THREE.Mesh(new THREE.CylinderGeometry(19, 13, 3.4, 24), new THREE.MeshStandardMaterial({ color: "#2a3242", roughness: 0.5, metalness: 0.6 }));
      const dome = new THREE.Mesh(new THREE.SphereGeometry(7, 18, 12, 0, Math.PI * 2, 0, Math.PI / 2), new THREE.MeshStandardMaterial({ color: "#7af0ff", emissive: "#30d0ff", emissiveIntensity: 0.9, transparent: true, opacity: 0.6 }));
      dome.position.y = 1.7;
      const rim = new THREE.Mesh(new THREE.TorusGeometry(17, 0.8, 8, 32), new THREE.MeshStandardMaterial({ color: "#00f0ff", emissive: "#00f0ff", emissiveIntensity: 1.4 }));
      rim.rotation.x = Math.PI / 2;
      saucer.add(hull, dome, rim);
      saucer.position.set(pc.cx + pc.radius, pc.baseY, pc.cz);
      this.mapGroup.add(saucer);
    }
    if (map.train) {
      const tc = map.train;
      const wagonMat = new THREE.MeshStandardMaterial({ color: "#c33a3a", roughness: 0.6, metalness: 0.3 });
      for (let i = 0; i < tc.wagons; i++) {
        const a = i * tc.arc;
        const wagon = new THREE.Mesh(new THREE.BoxGeometry(tc.half[0] * 2, 2.4, tc.half[1] * 2), wagonMat);
        wagon.position.set(tc.cx + tc.radius * Math.cos(a), tc.y + 1.2, tc.cz + tc.radius * Math.sin(a));
        wagon.rotation.y = -a - Math.PI / 2;
        this.mapGroup.add(wagon);
      }
    }
    if (map.id === "sandlot" && !this.noEdges) this.previewDragon = new DragonMesh(this.scene, 20);
    if (map.id === "medieval_castle" && !this.noEdges) {
      // the three european dragons soar in the preview exactly like in-match
      const palettes = [
        { body: "#3f6b30", membrane: "#26421f", belly: "#9aa06a" },
        { body: "#7a2833", membrane: "#4d1720", belly: "#b08a72" },
        { body: "#34363f", membrane: "#1e2027", belly: "#6a6f78" },
      ];
      for (let i = 0; i < 3; i++) {
        const rig = buildEuropeanDragon(null, palettes[i]);
        rig.group.scale.setScalar(3.0 + i * 0.5);
        this.scene.add(rig.group);
        this.euroDragons.push({
          rig, r: [70, 95, 118][i], y: [46, 60, 72][i],
          speed: [0.4, -0.3, 0.25][i], a: i * 2.1, // faster than in-game — a small tile reads slow motion poorly
          tint: palettes[i].body, model: null,
        });
      }
    }

    // frame the whole map
    const [mnx, , mnz] = map.bounds.min;
    const [mxx, , mxz] = map.bounds.max;
    this.center.set((mnx + mxx) / 2, 2, (mnz + mxz) / 2);
    const span = Math.max(mxx - mnx, mxz - mnz);
    this.radius = span * 0.72;
    this.height = span * 0.55;
    this.userAngle = null;
    this.mountRealModels(map);
    this.compileAsyncNow();
  }

  /** REAL MODELS in the preview too (owner 2026-11-04: "always everywhere"):
   *  parked horses/camels, the castle dragons and the kraken wear their GLB
   *  as soon as it is loaded. This context compiles its whole scene with
   *  compileAsync right after (compileAsyncNow), so a model that lands later
   *  is mounted then and the scene recompiled — a menu tile, no fight to hitch.
   *  The match's "armed" rule is about the GAME context and does not apply. */
  private mountRealModels(map: MapDef): void {
    if (!realModelsEnabled()) return;
    const apply = (): number => {
      let n = 0;
      for (const v of this.extraVehicles) if (v.m.attachLoadedModel()) n++;
      if (dragonModelLoaded()) {
        for (const d of this.euroDragons) if (!d.model) { d.model = mountDragonModel(d.rig.group, d.tint); n++; }
      }
      const kraken = seaModelIfReady(KRAKEN_URL);
      for (let i = 0; i < this.extraMonsters.length; i++) {
        const m = this.extraMonsters[i];
        if (m.kind !== "kraken" || !kraken || m.hasRealModel) continue;
        const kr = new MonsterMesh(this.scene, "kraken");
        if (!kr.hasRealModel) { kr.dispose(this.scene); continue; }
        kr.update(30, -2.2, 40, Math.PI * 0.25, 0, 0, 1);
        kr.group.scale.setScalar(1.6);
        m.dispose(this.scene);
        this.extraMonsters[i] = kr;
        n++;
      }
      return n;
    };
    apply();
    const kinds = new Set((map.vehicles ?? []).map((v) => v.kind));
    const loads: Promise<unknown>[] = [];
    if (kinds.has("horse")) loads.push(loadHorseModel());
    if (kinds.has("camel")) loads.push(loadCamelModel());
    if (this.euroDragons.length) loads.push(loadDragonModel());
    if (this.extraMonsters.some((m) => m.kind === "kraken")) loads.push(preloadSeaModel(KRAKEN_URL));
    if (!loads.length) return;
    const id = map.id;
    void Promise.all(loads).then(() => {
      if (this.gone || this.loadedMapId !== id) return;
      if (apply()) this.compileAsyncNow();
    });
  }

  private compileAsyncNow(): void {
    const token = ++this.compileToken;
    this.compiling = true;
    const done = (): void => {
      if (token !== this.compileToken) return;
      this.compiling = false;
      // a REAL preview has now compiled (and so references) its programs — the
      // parked warm-up scene can finally let go of its copies
      const pk = MapPreview3D.parked;
      if (pk && pk !== this) { MapPreview3D.parked = null; pk.deepDispose(pk.scene); pk.scene.clear(); }
    };
    const ang = this.userAngle ?? this.t * 0.25;
    this.camera.position.set(this.center.x + Math.cos(ang) * this.radius, this.center.y + this.height, this.center.z + Math.sin(ang) * this.radius);
    this.camera.lookAt(this.center);
    this.compiled = new Promise<void>((resolve) => {
      const finish = (): void => { done(); resolve(); };
      this.renderer.compileAsync(this.scene, this.camera).then(finish, finish);
      window.setTimeout(finish, 4000); // never leave the tile frozen on a stuck compile
    });
  }

  start(): void { if (!this.running) { this.running = true; this.bornAt = performance.now(); this.loop(); } }
  private bornAt = 0;

  private loop = (): void => {
    if (!this.running) return;
    if (!this.canvas.isConnected) {
      // GRACE: screens start the loop BEFORE mounting their DOM — killing the
      // context on that first frame was the lobby's "broken image" preview
      // (forceContextLoss on a canvas that mounts a tick later). Only a
      // canvas that STAYS disconnected is truly gone.
      if (performance.now() - this.bornAt > 3000) { this.dispose(); return; }
      this.raf = requestAnimationFrame(this.loop);
      return;
    }
    // reduced-quality profile: render every Nth vsync (animation time still
    // advances at real speed — t steps by the skipped frames' worth)
    this.frameNo++;
    if (this.frameDiv > 1 && this.frameNo % this.frameDiv !== 0) {
      this.raf = requestAnimationFrame(this.loop);
      return;
    }
    this.t += 0.016 * this.frameDiv;
    if (this.previewPracticePlatforms) {
      for (let i = 0; i < this.previewPracticePlatforms.movers.length; i++) {
        const [x, y, z] = practiceMoverPose(this.previewPracticePlatforms.movers[i], this.t);
        this.previewPracticePlatforms.group.children[i]?.position.set(x, y, z);
      }
    }
    for (const pk of this.extraPickups) {
      let x = pk.x, y = pk.y, z = pk.z;
      if (pk.attach) {
        const pose = practiceMoverPose(pk.attach.mover, this.t);
        x = pose[0] + pk.attach.off[0];
        y = pose[1] + pk.attach.off[1];
        z = pose[2] + pk.attach.off[2];
      }
      pk.m.update(x, y, z, true, 0.016, this.t);
    }
    for (const v of this.extraVehicles) {
      let x = v.x, y = v.y, z = v.z;
      if (v.attach) {
        const pose = practiceMoverPose(v.attach.mover, this.t);
        x = pose[0] + v.attach.off[0];
        y = pose[1] + v.attach.off[1];
        z = pose[2] + v.attach.off[2];
      }
      v.m.update(x, y, z, v.yaw, 0, 0, 0, false);
    }
    if (this.previewDragon) {
      this.dragonAng += 0.016 * 0.4;
      const dx2 = Math.cos(this.dragonAng) * 30, dz2 = 186 + Math.sin(this.dragonAng) * 30;
      this.previewDragon.update(dx2, 26 + Math.sin(this.dragonAng * 1.7) * 4, dz2, Math.PI - this.dragonAng, 0, 0.016);
    }
    for (const d of this.euroDragons) {
      d.model?.update(0.016 * this.frameDiv, 1, 3.3);
      d.a += d.speed * 0.016;
      d.rig.group.position.set(Math.cos(d.a) * d.r, d.y + Math.sin(d.a * 2.2) * 2.4, Math.sin(d.a) * d.r);
      d.rig.group.rotation.y = -d.a + (d.speed > 0 ? Math.PI / 2 : -Math.PI / 2);
      d.rig.group.rotation.z = (d.speed > 0 ? 1 : -1) * 0.16;
      const flap = Math.sin(this.t * 3.3 + d.r) * 0.42 + 0.08;
      d.rig.wingR.rotation.z = flap;
      d.rig.wingL.rotation.z = flap;
    }
    if (this.randomMark) this.randomMark.rotation.y += 0.016 * 1.3;
    this.previewDiscoDyn.length = 0;
    this.previewDisco?.sync(this.t, Math.max(0, this.t - 0.016), this.previewDiscoDyn, this.previewDiscoPool);
    const ang = this.userAngle ?? this.t * 0.25;
    this.camera.position.set(this.center.x + Math.cos(ang) * this.radius, this.center.y + this.height, this.center.z + Math.sin(ang) * this.radius);
    this.camera.lookAt(this.center);
    const w = this.canvas.clientWidth || 400, h = this.canvas.clientHeight || 300;
    if (settleSize(this.sizeSt, w, h, performance.now())) {
      this.camera.aspect = w / h; this.camera.updateProjectionMatrix(); this.renderer.setSize(w, h, false);
    }
    if (!this.compiling) {
      this.renderer.render(this.scene, this.camera);
      blitPreview(this.renderer, this.blit);
    }
    this.raf = requestAnimationFrame(this.loop);
  };

  /** dispose()/parkWarm() ran: late model loads must not touch this scene. */
  private gone = false;
  dispose(): void {
    this.gone = true;
    this.running = false;
    cancelAnimationFrame(this.raf);
    window.removeEventListener("pointerup", this.onPointerUp);
    window.removeEventListener("pointermove", this.onPointerMove);
    // The context is POOLED and outlives this preview (it used to be
    // forceContextLoss()ed, which freed everything): free the map's buffers
    // and materials explicitly, or every Play/lobby visit leaks the last map.
    this.clearMapContent();
    this.deepDispose(this.scene); // + the lights / random "?" mark
    this.randomMark = null;
    this.scene.clear();
    releasePreviewGl(this.gl);
  }

  /** Warm-up only (ui/mapPreview.ts warmMapPreview): hand the GL context back
   *  to the pool but KEEP this scene's materials alive. three.js deletes a
   *  compiled program as soon as no material uses it, so disposing here would
   *  throw the warm-up's compiles away; parked, they stay in the context's
   *  program cache until the first real preview has compiled the same ones. */
  private static parked: MapPreview3D | null = null;
  parkWarm(): void {
    this.gone = true;
    this.running = false;
    cancelAnimationFrame(this.raf);
    window.removeEventListener("pointerup", this.onPointerUp);
    window.removeEventListener("pointermove", this.onPointerMove);
    const prev = MapPreview3D.parked;
    if (prev && prev !== this) { prev.deepDispose(prev.scene); prev.scene.clear(); }
    MapPreview3D.parked = this;
    releasePreviewGl(this.gl);
  }
}

export interface CharacterCosmetics {
  bodySkin?: string;
  glowColor?: string;
  headgear?: string;
  knifeSkin?: string;
  skinUrl?: string;
}

export class CharacterPreview {
  private renderer: THREE.WebGLRenderer;
  private sizeSt: SizeState = { w: 0, h: 0, pw: 0, ph: 0, since: 0 };
  private gl: PooledGl;
  private blit: CanvasRenderingContext2D | null;
  private scene = new THREE.Scene();
  private camera: THREE.PerspectiveCamera;
  private avatar: Avatar;
  private daisGlow: THREE.Mesh;
  private running = false;
  private raf = 0;
  private t = 0;
  private spin = 0;
  private dragging = false;
  private lastX = 0;

  constructor(private canvas: HTMLCanvasElement, private cos: CharacterCosmetics = {}) {
    this.gl = acquirePreviewGl(true);
    this.renderer = this.gl.renderer;
    this.blit = canvas.getContext("2d");
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(canvas.clientWidth || 340, canvas.clientHeight || 420, false);
    // Draw-call readout for agent/shot.mjs. This scene is ONE avatar on a dais
    // with a fixed camera — the only place in the game where "draw calls per
    // avatar" is a controlled measurement rather than a guess about whatever
    // happened to be on screen. One property write at construction.
    (window as unknown as { npPreviewInfo?: THREE.WebGLRenderer["info"] }).npPreviewInfo = this.renderer.info;

    this.scene.background = new THREE.Color("#080b14");
    this.scene.fog = new THREE.Fog("#080b14", 8, 24);
    this.camera = new THREE.PerspectiveCamera(42, (canvas.clientWidth || 340) / (canvas.clientHeight || 420), 0.1, 100);
    // Framed to the CHARACTER, not to the room: at z=5.2 with a 42° FOV the
    // operative was ~200 px tall in a 340x420 panel — a doll on a stage. This
    // is the only fixed-camera look at your own avatar in the whole game.
    this.camera.position.set(0, 1.25, 3.5);
    this.camera.lookAt(0, 1.05, 0);
    // GEAR_DEMO parachute: the canopy sits 5 m up, far outside the character
    // framing, so pull back for that capture only (agent-only, see buildAvatar).
    if (GEAR_DEMO.includes("parachute") || GEAR_DEMO === "all") {
      this.camera.position.set(0, 3.2, 9.5);
      this.camera.lookAt(0, 2.6, 0);
    }
    // the jetpack rides the BACK, which the default turntable start faces away
    // from — start it half a turn round so a capture sees the hardware
    if (GEAR_DEMO.includes("jetpack")) this.spin = Math.PI * 0.85;

    // Three-point rig. The old one was a blue hemisphere plus a cyan and a
    // magenta point light: pure neon, no white, so the body read as a dark
    // silhouette with two coloured rims and no surface at all — which also made
    // every cosmetic body colour look the same. A neutral key does the shaping,
    // the two neon lamps stay as rims for the game's identity.
    this.scene.add(new THREE.HemisphereLight("#8fa8cc", "#141824", 1.5));
    const sun = new THREE.DirectionalLight("#ffffff", 2.6); sun.position.set(2.5, 4.5, 4); this.scene.add(sun);
    const key = new THREE.PointLight("#00f0ff", 28, 30); key.position.set(3, 4, 4); this.scene.add(key);
    const rim = new THREE.PointLight("#ff2ec4", 30, 30); rim.position.set(-4, 3, -2); this.scene.add(rim);
    const grid = new THREE.GridHelper(40, 40, "#00f0ff", "#12233a");
    (grid.material as THREE.Material).transparent = true; (grid.material as THREE.Material).opacity = 0.3;
    this.scene.add(grid);
    const dais = new THREE.Mesh(new THREE.CylinderGeometry(1.3, 1.5, 0.2, 32), new THREE.MeshBasicMaterial({ color: "#0a1420" }));
    dais.position.y = 0.1; this.scene.add(dais);
    this.daisGlow = new THREE.Mesh(new THREE.RingGeometry(1.4, 1.65, 48), new THREE.MeshBasicMaterial({ color: "#00f0ff", transparent: true, opacity: 0.7, side: THREE.DoubleSide, blending: THREE.AdditiveBlending }));
    this.daisGlow.rotation.x = -Math.PI / 2; this.daisGlow.position.y = 0.21; this.scene.add(this.daisGlow);

    this.avatar = this.buildAvatar();

    canvas.addEventListener("pointerdown", (e) => { this.dragging = true; this.lastX = e.clientX; });
    // window-level listeners MUST be removed in dispose() — as anonymous
    // closures they retained the whole preview (scene + Avatar + renderer)
    // forever, once per cosmetics/skin-editor open. Same fix as MapPreview3D.
    this.onPointerUp = () => (this.dragging = false);
    this.onPointerMove = (e: PointerEvent) => {
      if (!this.dragging) return;
      this.spin += (e.clientX - this.lastX) * 0.012;
      this.lastX = e.clientX;
    };
    window.addEventListener("pointerup", this.onPointerUp);
    window.addEventListener("pointermove", this.onPointerMove);
  }

  private onPointerUp: () => void = () => {};
  private onPointerMove: (e: PointerEvent) => void = () => {};

  private buildAvatar(): Avatar {
    const c = this.cos;
    const a = new Avatar(this.scene, {
      bodyColor: cosmeticValue("bodySkin", c.bodySkin),
      glowColor: cosmeticValue("glowColor", c.glowColor),
      headgear: c.headgear && c.headgear !== "head_none" ? cosmeticValue("headgear", c.headgear) : "",
      skinUrl: c.skinUrl,
    });
    a.update(0, 0.2, 0, Math.PI, false); // face the camera (-z toward viewer)
    // GEAR_DEMO (agent-only, same family as match.ts's fxdemo/gundemo): dress
    // the preview operative in worn cosmetics so agent/shot.mjs can photograph
    // them. The pirate tricorn, the hordebearer skull, the cowboy set, the
    // jetpack and the drop-in parachute are each gated on a specific map, a
    // pickup or a carried objective, none of which a headless capture run can
    // reach — which is why no iteration of this project had ever photographed
    // any of them. Cosmetic only, and only on THIS local preview scene.
    if (GEAR_DEMO) {
      const want = (k: string): boolean => GEAR_DEMO === "all" || GEAR_DEMO.split(",").includes(k);
      if (want("pirate")) a.setPirateGear(true);
      if (want("skull")) a.setSkullFace(true);
      if (want("cowboy")) a.setCowboy(true);
      if (want("jetpack")) a.setJetpack(true);
      if (want("parachute")) a.setParachute(true);
      // "gun" / "gun:sniper" ARMS the preview operative and runs a live
      // update() in the loop (2026-08-01, the HANDS pass). The cosmetics stage
      // is the only FIXED CAMERA on an avatar in the entire game, and it was
      // the one place the weapon hold — now the whole point of the arm rig —
      // could not be photographed, because the preview avatar is unarmed and
      // its pose is a single static update() at build time.
      const gun = GEAR_DEMO.split(",").find((k) => k.startsWith("gun"));
      if (gun) { a.setWeapon(gun.split(":")[1] || "rifle"); this.demoArmed = true; }
    }
    return a;
  }
  /** agent-only, see above: drive the real gait/hold animation in the preview. */
  private demoArmed = false;

  /** Rebuild the avatar to reflect changed cosmetics / skin (live preview). */
  setCosmetics(cos: CharacterCosmetics): void {
    this.cos = { ...this.cos, ...cos };
    this.avatar.dispose(this.scene);
    this.avatar = this.buildAvatar();
  }

  start(): void { if (!this.running) { this.running = true; this.bornAt = performance.now(); this.loop(); } }
  private bornAt = 0;

  private loop = (): void => {
    if (!this.running) return;
    if (!this.canvas.isConnected) {
      // same pre-mount grace as MapPreview3D (see above)
      if (performance.now() - this.bornAt > 3000) { this.dispose(); return; }
      this.raf = requestAnimationFrame(this.loop);
      return;
    }
    this.t += 0.016;
    const yaw = Math.PI + (GEAR_SPIN !== null ? GEAR_SPIN
      : this.dragging || this.spin ? this.spin : this.t * 0.5);
    if (this.demoArmed) {
      // agent-only capture mode: walk the real gait + hold pose (and fire every
      // ~1.4 s so the recoil kick is on screen), instead of the static build-
      // time pose the shipped preview uses.
      const s = this.t * 0.55;
      this.avatar.update(Math.sin(s) * 0.35, 0.2, Math.cos(s) * 0.35, yaw, false, Math.sin(this.t * 0.7) * 0.45);
      if (Math.floor(this.t / 1.4) !== Math.floor((this.t - 0.016) / 1.4)) this.avatar.recoil(1);
    } else {
      this.avatar.group.rotation.y = yaw;
      this.avatar.group.position.y = 0.2 + Math.sin(this.t * 1.2) * 0.02;
    }
    (this.daisGlow.material as THREE.MeshBasicMaterial).opacity = 0.5 + Math.sin(this.t * 2) * 0.22;
    // keep the canvas matched to its container size
    const w = this.canvas.clientWidth || 340, h = this.canvas.clientHeight || 420;
    if (settleSize(this.sizeSt, w, h, performance.now())) {
      this.camera.aspect = w / h; this.camera.updateProjectionMatrix(); this.renderer.setSize(w, h, false);
    }
    this.renderer.render(this.scene, this.camera);
    blitPreview(this.renderer, this.blit);
    this.raf = requestAnimationFrame(this.loop);
  };

  dispose(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
    window.removeEventListener("pointerup", this.onPointerUp);
    window.removeEventListener("pointermove", this.onPointerMove);
    this.avatar.dispose(this.scene);
    // pooled context outlives us: free the dais / glow ring / grid too
    this.scene.traverse((o) => {
      const m = o as THREE.Mesh;
      m.geometry?.dispose?.();
      const mats = Array.isArray(m.material) ? m.material : m.material ? [m.material] : [];
      for (const mat of mats) mat.dispose();
    });
    this.scene.clear();
    releasePreviewGl(this.gl);
  }
}
